import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Link,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { forgotPassword, forgotPasswordSubmit } from '../../services/auth.ts';
import Logo from '../common/Logo.tsx';

interface ForgotPasswordProps {
  onNavigateToLogin: () => void;
  onPasswordResetComplete: () => void;
}

const FormContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  maxWidth: '400px',
  width: '100%',
  background: 'rgba(26, 26, 46, 0.7)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(138, 43, 226, 0.2)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
}));

const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
  background: 'linear-gradient(135deg, rgba(26, 26, 46, 0.9) 0%, rgba(50, 50, 80, 0.9) 100%)',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 80% 20%, rgba(138, 43, 226, 0.15) 0%, transparent 60%)',
    zIndex: -1,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: 'rgba(138, 43, 226, 0.3)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(138, 43, 226, 0.5)',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'rgba(138, 43, 226, 0.7)',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
  },
  '& .MuiInputBase-input': {
    color: '#fff',
  },
}));

const StyledStepper = styled(Stepper)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  width: '100%',
  '& .MuiStepLabel-label': {
    color: 'rgba(255, 255, 255, 0.7)',
  },
  '& .MuiStepLabel-label.Mui-active': {
    color: '#fff',
  },
  '& .MuiStepLabel-label.Mui-completed': {
    color: '#00CC99',
  },
  '& .MuiStepIcon-root': {
    color: 'rgba(138, 43, 226, 0.5)',
  },
  '& .MuiStepIcon-root.Mui-active': {
    color: '#8A2BE2',
  },
  '& .MuiStepIcon-root.Mui-completed': {
    color: '#00CC99',
  },
}));

const ForgotPassword: React.FC<ForgotPasswordProps> = ({
  onNavigateToLogin,
  onPasswordResetComplete,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const steps = ['Request Reset', 'Verify Code', 'Reset Complete'];

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await forgotPassword(email);
      setActiveStep(1);
    } catch (err: any) {
      console.error('Password reset request error:', err);
      setError(err.message || 'Failed to request password reset. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!code.trim()) {
      setError('Verification code is required');
      return;
    }
    
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await forgotPasswordSubmit(email, code, newPassword);
      setActiveStep(2);
      setTimeout(() => {
        onPasswordResetComplete();
      }, 2000);
    } catch (err: any) {
      console.error('Password reset error:', err);
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <form onSubmit={handleRequestReset}>
            <Typography variant="body1" sx={{ mb: 3, color: 'rgba(255, 255, 255, 0.7)' }}>
              Enter your email address and we&apos;ll send you a code to reset your password.
            </Typography>
            
            <StyledTextField
              label="Email"
              variant="outlined"
              fullWidth
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
            
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{
                mb: 2,
                py: 1.5,
                background: 'linear-gradient(45deg, #8A2BE2 30%, #00BFFF 90%)',
                color: 'white',
                '&:hover': {
                  background: 'linear-gradient(45deg, #7B27CC 30%, #00A6E0 90%)',
                },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Code'}
            </Button>
          </form>
        );
      case 1:
        return (
          <form onSubmit={handleResetPassword}>
            <Typography variant="body1" sx={{ mb: 3, color: 'rgba(255, 255, 255, 0.7)' }}>
              Enter the verification code sent to your email and your new password.
            </Typography>
            
            <StyledTextField
              label="Verification Code"
              variant="outlined"
              fullWidth
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
            />
            
            <StyledTextField
              label="New Password"
              variant="outlined"
              fullWidth
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              helperText="Password must be at least 8 characters"
              FormHelperTextProps={{ sx: { color: 'rgba(255, 255, 255, 0.5)' } }}
            />
            
            <StyledTextField
              label="Confirm Password"
              variant="outlined"
              fullWidth
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
            
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{
                mb: 2,
                py: 1.5,
                background: 'linear-gradient(45deg, #8A2BE2 30%, #00BFFF 90%)',
                color: 'white',
                '&:hover': {
                  background: 'linear-gradient(45deg, #7B27CC 30%, #00A6E0 90%)',
                },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
            </Button>
          </form>
        );
      case 2:
        return (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#00CC99' }}>
              Password Reset Successful!
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: 'rgba(255, 255, 255, 0.7)' }}>
              You will be redirected to the login page shortly.
            </Typography>
            <CircularProgress size={24} sx={{ color: '#00CC99' }} />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <PageContainer>
      <Box sx={{ mb: 4 }}>
        <Logo size="large" />
      </Box>
      
      <FormContainer>
        <Typography variant="h5" sx={{ mb: 3, color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>
          Reset Password
        </Typography>
        
        <StyledStepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </StyledStepper>
        
        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
        
        {renderStepContent()}
        
        {activeStep < 2 && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Remember your password?{' '}
              <Link
                component="button"
                type="button"
                onClick={onNavigateToLogin}
                sx={{ color: 'rgba(138, 43, 226, 0.8)', textDecoration: 'none' }}
              >
                Sign In
              </Link>
            </Typography>
          </Box>
        )}
      </FormContainer>
    </PageContainer>
  );
};

export default ForgotPassword; 